import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    List,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';

import AdminNodeStore from '../../../stores/admin-node-store';
import UserService from '../../../http/user-service';
import { DbNode } from '../../../types/node';
import { UserRole } from '../../../types/user';
import NodePickerItem from '../NodePickerItem';

//=================================================================================================
interface AddUserDialogProps {
    open: boolean;
    onClose: () => void;
    onAdded: () => void;
    rootNodeId: number;
}

const ROLE_LABELS: Record<UserRole, string> = {
    [UserRole.Administrator]: 'Администратор',
    [UserRole.User]: 'Пользователь',
};

const AddUserDialog = observer(({ open, onClose, onAdded, rootNodeId }: AddUserDialogProps) => {
    const [rootStore] = useState(() => new AdminNodeStore(rootNodeId));

    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [type, setType] = useState<UserRole>(UserRole.User);
    const [email, setEmail] = useState('');
    const [description, setDescription] = useState('');
    const [selectedNode, setSelectedNode] = useState<DbNode | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) rootStore.loadNode().catch(() => {});
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClose = () => {
        if (submitting) return;
        setLogin('');
        setPassword('');
        setType(UserRole.User);
        setEmail('');
        setDescription('');
        setSelectedNode(null);
        setError('');
        onClose();
    };

    const handleSubmit = async () => {
        if (!login.trim()) { setError('Введите логин'); return; }
        if (password.length < 5) { setError('Пароль должен содержать минимум 5 символов'); return; }
        if (!selectedNode) { setError('Выберите ноду'); return; }
        setSubmitting(true);
        setError('');
        try {
            await UserService.create({
                login: login.trim(),
                password,
                node_id: selectedNode.node_id,
                type,
                email: email.trim() || null,
                description: description.trim() || null,
            });
            onAdded();
            handleClose();
        } catch {
            setError('Не удалось создать пользователя');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="xs"
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !submitting) handleSubmit(); }}
        >
            <DialogTitle>Добавить пользователя</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
                <TextField
                    label="Логин"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    autoFocus
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Пароль"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    size="small"
                />
                <FormControl fullWidth size="small">
                    <InputLabel>Роль</InputLabel>
                    <Select label="Роль" value={type} onChange={(e) => setType(e.target.value as UserRole)}>
                        {(Object.values(UserRole).filter((v) => typeof v === 'number') as UserRole[]).map((r) => (
                            <MenuItem key={r} value={r}>{ROLE_LABELS[r]}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    label="Email (необязательно)"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    fullWidth
                    size="small"
                />
                <TextField
                    label="Описание (необязательно)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    size="small"
                    multiline
                    rows={2}
                />
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Нода</Typography>
                        {selectedNode && (
                            <Chip label={selectedNode.name} size="small" onDelete={() => setSelectedNode(null)} />
                        )}
                    </Box>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, maxHeight: 200, overflowY: 'auto' }}>
                        {rootStore.loading && rootStore.items.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={20} />
                            </Box>
                        ) : (
                            <List dense disablePadding sx={{ p: 0.5 }}>
                                {rootStore.items.map((node) => (
                                    <NodePickerItem
                                        key={node.node_id}
                                        node={node}
                                        depth={0}
                                        selectedNodeId={selectedNode?.node_id ?? null}
                                        onSelect={setSelectedNode}
                                    />
                                ))}
                            </List>
                        )}
                    </Box>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={submitting}>Отмена</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting} disableElevation>
                    {submitting ? <CircularProgress size={18} /> : 'Добавить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default AddUserDialog;
